import {z} from 'zod';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetWarehouseItemsForExportQuery} from '@/services/graphqlHooks';
import {useGetWarehouseItemsForExportQuery} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import InventoryOptionsFields from '../../components/export-options/InventoryOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import {formatExportDateTime} from '../exportOptions';
import {EXPORT_WAREHOUSE_PARAM} from '../exportParams';
import {fetchAllPages} from '../fetchAllPages';

export const inventoryOptionsSchema = z.object({
  warehouseId: z.string().min(1, i18n.t('Warehouse is required')),
});

export type InventoryOptions = z.infer<typeof inventoryOptionsSchema>;

type WarehouseItemExportRecord =
  GetWarehouseItemsForExportQuery['getWarehouseItems']['items'][number];

export const inventoryExport: ExportDefinition<
  InventoryOptions,
  WarehouseItemExportRecord
> = {
  id: 'inventory',
  label: i18n.t('Inventory'),
  permission: 'EXPORT_INVENTORY',
  entitlement: 'INVENTORY',
  optionsSchema: inventoryOptionsSchema,
  OptionsFields: InventoryOptionsFields,
  getDefaultOptions: searchParams => ({
    warehouseId: searchParams.get(EXPORT_WAREHOUSE_PARAM) ?? '',
  }),
  load: ({warehouseId}) =>
    fetchAllPages(async nextToken => {
      const variables = {warehouseId, nextToken};
      const {getWarehouseItems} = await queryClient.fetchQuery({
        queryKey: useGetWarehouseItemsForExportQuery.getKey(variables),
        queryFn: useGetWarehouseItemsForExportQuery.fetcher(
          graphqlClient,
          variables
        ),
      });

      return getWarehouseItems;
    }),
  columns: [
    {header: i18n.t('Product name'), value: item => item.name},
    {header: i18n.t('Quantity'), value: item => item.quantity},
    {
      header: i18n.t('Updated at'),
      value: item => formatExportDateTime(item.updatedAt),
    },
  ],
  getFileName: () => 'inventory',
};
